/**
 * Où l'on était, pour qu'une actualisation y ramène.
 *
 * *Chris, 28/09/2026 : « lorsque l'on actualise une page cela remet automatiquement l'utilisateur
 * sur le premier chantier et sur devis figé ; quand on actualise il faut rester précisément là où
 * l'on se trouve pour ne pas avoir à refaire le chemin ».*
 *
 * **Le stockage de SESSION, et non le stockage local** : il survit à l'actualisation mais appartient
 * à l'onglet. Deux onglets ouverts sur deux chantiers gardent chacun le leur, et un onglet neuf
 * s'ouvre comme avant, sur le premier chantier. Rien de ce qui est gardé ici n'est une donnée : ce
 * sont des identifiants et des positions d'écran, relus puis vérifiés contre ce que le serveur rend
 * — un chantier supprimé entre-temps ne s'ouvre pas, on retombe sur le premier.
 *
 * Toute lecture et toute écriture sont protégées : un navigateur en navigation privée ou aux
 * données bloquées lève une exception, et l'application doit alors simplement s'ouvrir comme avant.
 */

const CLE = "planif.position";

export type Position = {
  readonly ecran?: string;
  readonly chantierId?: string;
  readonly versionId?: string;
  readonly defilement?: number;
};

function lire<T>(cle: string): T | undefined {
  try {
    const brut = window.sessionStorage.getItem(cle);
    return brut === null ? undefined : (JSON.parse(brut) as T);
  } catch {
    return undefined;
  }
}

function ecrire(cle: string, valeur: unknown): void {
  try {
    window.sessionStorage.setItem(cle, JSON.stringify(valeur));
  } catch {
    // Stockage refusé : on perd la mémoire de la position, rien d'autre.
  }
}

export function lirePosition(): Position {
  const p = lire<Position>(CLE);
  return typeof p === "object" && p !== null ? p : {};
}

/** Fusionne : chaque appelant n'écrit que ce qu'il connaît. */
export function retenirPosition(partielle: Position): void {
  ecrire(CLE, { ...lirePosition(), ...partielle });
}

/** Un ensemble de clés (tâches dépliées, chapitres ouverts), rangé par version. */
export function lireEnsemble(nom: string, versionId: string): ReadonlySet<string> {
  const liste = lire<unknown>(`planif.${nom}.${versionId}`);
  return new Set(Array.isArray(liste) ? liste.filter((x): x is string => typeof x === "string") : []);
}

export function retenirEnsemble(nom: string, versionId: string, ensemble: ReadonlySet<string>): void {
  ecrire(`planif.${nom}.${versionId}`, [...ensemble]);
}

/** Le défilement d'un cadre, rangé par version. */
export function lireDefilement(nom: string, versionId: string): { haut: number; gauche: number } | undefined {
  const d = lire<{ haut?: unknown; gauche?: unknown }>(`planif.${nom}.${versionId}`);
  if (typeof d !== "object" || d === null) return undefined;
  return {
    haut: typeof d.haut === "number" ? d.haut : 0,
    gauche: typeof d.gauche === "number" ? d.gauche : 0,
  };
}

export function retenirDefilement(nom: string, versionId: string, haut: number, gauche: number): void {
  ecrire(`planif.${nom}.${versionId}`, { haut, gauche });
}
